import { cn } from '@/lib/utils'
import { RowModel } from '@tanstack/react-table'
import { useCallback, useRef, useState } from 'react'
import { useTableApiContext } from '../../utils/table-context'
import { GridColDefPro, GridRowId, ValidRowModel } from '../../utils/types'
import EditableDateCell from './EditableDateCell'

const commonClasses = [
        'cursor-text size-full bg-transparent border-none flex items-center',
        'hover:bg-foreground/25 overflow-hidden min-w-0 px-1 rounded-sm',
    ],
    inheritedStyles: React.CSSProperties = {
        fontSize: 'inherit',
        fontFamily: 'inherit',
        fontWeight: 'inherit',
        maxHeight: 'calc(100% - 2px)',
        color: 'inherit',
    }

interface IPropsEditableCell<TData extends ValidRowModel = any> {
    type: GridColDefPro['type']
    defaultValue: any
    field: string
    id: GridRowId
    row: RowModel<TData>
}

export default function EditableCell({
    type,
    defaultValue,
    field,
    id,
    row,
}: IPropsEditableCell) {
    const { onCellEditCommit } = useTableApiContext()
    const [isEditing, setEditing] = useState(false)
    const [value, setValue] = useState(defaultValue)
    const previousValueRef = useRef(defaultValue)

    const inputType = (() => {
        switch (type) {
            case 'datetime-local':
            case 'time':
            case 'number':
                return type
            case 'SN':
            case 'boolean':
                return 'checkbox'
            default:
                return 'text'
        }
    })()

    const cellId = `editable-cell-input-${String(id)}-${field}`

    const onCommit = useCallback(() => {
        setEditing(false)
        if (value !== previousValueRef.current) {
            onCellEditCommit?.({
                id,
                field,
                value,
                row,
            })
            previousValueRef.current = value
        }
    }, [value, id, field, row, onCellEditCommit])

    const onCancel = useCallback(() => {
        setValue(previousValueRef.current)
        setEditing(false)
    }, [])

    if (type === 'date')
        return (
            <EditableDateCell
                id={cellId}
                value={value}
                className={cn(...commonClasses)}
                style={inheritedStyles}
                onCommit={novoValor => {
                    setValue(novoValor)
                    if (novoValor === previousValueRef.current) return
                    onCellEditCommit?.({ id, field, value: novoValor, row })
                    previousValueRef.current = novoValor
                }}
            />
        )

    if (!isEditing)
        return (
            <input
                id={cellId}
                readOnly
                type={inputType}
                value={value}
                placeholder=" "
                className={cn(...commonClasses, 'outline-none')}
                style={inheritedStyles}
                onDoubleClick={() => setEditing(true)}
                onFocus={() => setEditing(true)}
            />
        )

    return (
        <EditableCellDefault
            id={cellId}
            inputType={inputType}
            onCommit={onCommit}
            onCancel={onCancel}
            value={value}
            setValue={setValue}
        />
    )
}

interface IPropsDefaultEditableCellDefault {
    inputType: string
    value: IPropsEditableCell['defaultValue']
    setValue: React.Dispatch<React.SetStateAction<any>>
    onCommit: () => void
    onCancel: () => void
    id: string
}

function EditableCellDefault({
    inputType,
    onCommit,
    onCancel,
    value,
    setValue,
    id: cellId,
}: IPropsDefaultEditableCellDefault) {
    return (
        <input
            id={cellId}
            autoFocus
            type={inputType}
            onBlur={onCommit}
            onKeyDown={e => {
                if (e.key === 'Enter') {
                    e.currentTarget.blur()
                }
                if (e.key === 'Escape') {
                    e.preventDefault()
                    e.stopPropagation()
                    onCancel()
                }
            }}
            value={value}
            onChange={e => {
                setValue(e.target.value)
            }}
            className={cn(...commonClasses, 'bg-foreground/25 outline-1')}
            style={inheritedStyles}
        />
    )
}
